<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { Button } from "@/shared/ui";

import type { Group } from "../model/store";
import { useServicesCatalogStore } from "../model/store";
import CategoryRow from "./CategoryRow.vue";
import ServiceDraftRow from "./ServiceDraftRow.vue";
import ServiceRow from "./ServiceRow.vue";

const props = defineProps<{
  group: Group;
}>();

const emit = defineEmits<{
  open: [serviceId: string];
}>();

const { t } = useI18n();
const store = useServicesCatalogStore();

const articleRef = ref<HTMLElement | null>(null);

const ariaLabel = computed(() =>
  props.group.category !== null
    ? props.group.category.name
    : t("services.categories.uncategorized"),
);

const serviceCount = computed(() => props.group.rows.length);

function onRowRemoved(): void {
  const trigger = articleRef.value?.querySelector<HTMLElement>(
    '[data-testid="add-service-trigger"]',
  );
  if (trigger) {
    trigger.focus();
  } else {
    // Uncategorized group: focus the article itself as fallback
    articleRef.value?.focus();
  }
}
</script>

<template>
  <article
    ref="articleRef"
    role="group"
    tabindex="-1"
    :aria-label="ariaLabel"
    class="border border-border rounded-md overflow-hidden mb-4"
  >
    <CategoryRow
      :category="group.category"
      :serviceCount="serviceCount"
    />

    <!-- Info banner for uncategorized group (above rows) -->
    <p
      v-if="group.category === null"
      data-testid="uncategorized-hint"
      class="px-3 py-2 text-sm text-muted-foreground"
    >
      {{ t("services.categories.assignHint") }}
    </p>

    <div
      v-if="group.category === null || !store.isCategoryCollapsed(group.category.id)"
      class="cat-body"
    >
      <!-- Service rows list: ServiceRow renders its own <li> -->
      <ul v-if="group.rows.length > 0">
        <ServiceRow
          v-for="row in group.rows"
          :key="row.kind === 'saved' ? row.service.id : row.tempId"
          :row="row"
          @open="(id) => emit('open', id)"
          @removed="onRowRemoved"
        />
      </ul>

      <!-- Empty-category hint (categorized, no rows, draft closed) -->
      <template
        v-else-if="group.category !== null && !store.isDraftOpen(group.category.id)"
      >
        <p
          data-testid="empty-category-hint"
          class="px-3 py-2 text-sm text-muted-foreground"
        >
          {{ t("services.empty.category") }}
        </p>
      </template>

      <!-- Draft input (categorized, draft open) -->
      <ServiceDraftRow
        v-if="group.category !== null && store.isDraftOpen(group.category.id)"
        :categoryId="group.category.id"
      />

      <!-- Add-service trigger (categorized, draft closed) -->
      <Button
        v-if="group.category !== null && !store.isDraftOpen(group.category.id)"
        data-testid="add-service-trigger"
        variant="ghost"
        size="sm"
        class="w-full justify-start"
        @click="store.openDraft(group.category.id)"
      >
        + {{ t("services.actions.addService") }}
      </Button>
    </div>
  </article>
</template>
